import _ from 'lodash';
import hash from 'object-hash';
import { columnsToCamelCase, Row, Table, cellNames } from '@/main/utils/table';
import {
	UPDATE_BASIC_TABLE_SCHEMA, UPDATE_BASIC_TABLE_DATA, STORE_TABLE,
	ROW_DETAIL_LOADING, UPDATE_BASIC_EDIT, END_LOADING, START_LOADING,
	UPDATE_EDIT_HASH, UPDATE_BASIC_TABLE_SCHEMA_DATA, START_LOADING_V2, END_LOADING_V2, SAVE_SORT,
} from '@/main/store/listEditTypes';
import { Status } from '@/main/store/@types/page';
import { dataMapper } from '@/main/utils/dataMapper';
import { EditStoreBase } from '@/main/store/@types/edit.d';
import { objectToCamelCaseShallow } from '@/main/utils/stringHelper';
import { SchemaMutationInput, TableDataMutationInput } from '@/main/store/@types/store.d';


export const mutations = {
	// #region Edit mutations
	/**
	 * Update following component's properties: dropdownList, operations and
	 * ValidationRules, if defined on data (those will be deleted), and Edit,
	 * to which `data` will be assigned.
	 *
	 * @param {Object} state
	 * @param {String} stateKey - name of the updated component.
	 * @param {Object} data - new data for edit
	 */
	[UPDATE_BASIC_EDIT](state: EditStoreBase, { data }: { data: any }) {
		data.DropDownList && (state.dropdownList = { ...(state.dropdownList ?? {}), ...data.DropDownList });
		delete data.DropDownList;
		data.Operations && (state.operations = data.Operations);
		delete data.Operations;
		if (data.ValidationRules) {
			if (!_.isEmpty(data.ValidationRules)) {
				state.validationRules = data.ValidationRules;
			}
			delete data.ValidationRules;
		}
		dataMapper(state.edit, data);
	},
	[UPDATE_EDIT_HASH](state: EditStoreBase, { data }: { data: any }) {
		state.editHash = hash(state.edit);
		state.status = Status.ok;
	},
	// #endregion


	// #region table mutations
	/**
	 * Updates table schema of table `tableName` of component `stateKey`.
	 *
	 * @param {Object} state
	 * @param {String} stateKey - name of the updated component.
	 * @param {Object} data - table data.
	 * @param {String} tableName - name of the table, default `[STORE_TABLE]`.
	 */

	[UPDATE_BASIC_TABLE_SCHEMA](state: EditStoreBase, { data, tableName = STORE_TABLE }: SchemaMutationInput) {
		const table = _.get(state.dependentTables, tableName) as Table;
		table.columns = columnsToCamelCase(data.Columns);
		table.cellNames = cellNames(table);
		table.gridId = data.GridId;
		table.operations = data.Operations;
		table.hasIdColumn = data.HasIdColumn;

		(table.isEditable) && (table.hash = hash(table.rows));

		table.status = Status.ok;
	},
	/**
	 * Updates table data of table `tableName` of component `stateKey`.
	 *
	 * @param {Object} state
	 * @param {String} stateKey - name of the updated component.
	 * @param {Object} data - table data.
	 * @param {String} tableName - name of the table, default `[STORE_TABLE]`.
	 */
	[UPDATE_BASIC_TABLE_DATA](state: EditStoreBase, { data, tableName = STORE_TABLE, isEditable = false }: TableDataMutationInput) {
		const table = _.get(state.dependentTables, tableName) as Table;

		table.status = Status.ok;
		_.isArray(data) && (table.rows = data.map((row: Row) => objectToCamelCaseShallow(row)));
		(table.isEditable || isEditable) && (table.hash = hash(table.rows));
	},

	[UPDATE_BASIC_TABLE_SCHEMA_DATA](state: EditStoreBase, { data, tableName = STORE_TABLE, isEditable = false }: TableDataMutationInput) {
		const table = _.get(state.dependentTables, tableName) as Table;

		table.columns = columnsToCamelCase(data.Columns);
		table.cellNames = cellNames(table);
		table.operations = data.Operations;
		table.hasIdColumn = data.HasIdColumn;
		table.status = Status.ok;

		table.rows = data.Rows.map((row: Row) => objectToCamelCaseShallow(row));
		(table.isEditable || isEditable) && (table.hash = hash(table.rows));

		table.status = Status.ok;
	},

	[ROW_DETAIL_LOADING](state: EditStoreBase, { tableName = 'rowDetail', loading }: { tableName: string, loading: boolean }) {
		state.dependentTables[tableName].loadingCount += loading ? 1 : -1;
	},
	[SAVE_SORT](state: EditStoreBase, { data, tableName = STORE_TABLE }: { data: { columnId: any; order: string }, tableName: string; }) {
		const table = _.get(state.dependentTables, tableName) as Table;
		table.sort = data;
	},
	// #endregion

	[START_LOADING](state: EditStoreBase, tableName?: string) {
		(tableName
			? state.dependentTables[tableName] || <Table>(<any>state.localState)[tableName]
			: state).loadingCount += 1;
	},

	[END_LOADING](state: EditStoreBase, tableName?: string) {
		(tableName
			? state.dependentTables[tableName] || <Table>(<any>state.localState)[tableName]
			: state).loadingCount -= 1;
	},

	[START_LOADING_V2](state: EditStoreBase, uri: string = '') {
		_.get(state, uri, state).loadingCount += 1;
	},

	[END_LOADING_V2](state: EditStoreBase, uri: string = '') {
		const obj = _.get(state, uri, state);
		obj.loadingCount -= 1;
		if (obj.loadingCount < 0) obj.loadingCount = 0;
	},

	beforeRefresh(state: EditStoreBase) {
		state.dmsTabs = [];
	},

	updateTableRow(state: EditStoreBase, { tableName, rowId, row }: { tableName: string, rowId: number, row?: Row }) {
		const table = _.get(state.dependentTables, tableName, null) as Table | null;
		if (!table) {
			console.error(`Table not found, ${tableName}`);
			return;
		}
		const filteredRows = table.rows.filter((item) => item.cells[table.cellNames!.idColumn!] !== rowId);
		table.rows = (row) ? [...filteredRows, row] : filteredRows;
	},
};
